import { Color } from "@repo/app-types/constants/colors";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import {
  getOutcomeAmountFromDefinitionAndGrading,
  getOutcomeResultForValueId,
  getOutcomeValueFromDefinitionAndGrading,
  greenRedYellowResultTypeCategory,
  isRevenueOutcomeDefinition,
  type GradingOutcome,
} from "@repo/app-types/entities/outcome";
import type { Size } from "@repo/ui/helpers/helpers";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import type { FC } from "react";

export interface OutcomeValuePillProps {
  size: Size;
  outcome: GradingOutcome | null | undefined;
  outcomeDefinition: OutcomeDefinition;
}

// Map outcome categories to pill colors
const categoryToPillColor: Record<string, PillColor> = {
  [greenRedYellowResultTypeCategory.green]: Color.Green,
  [greenRedYellowResultTypeCategory.red]: Color.Red,
  [greenRedYellowResultTypeCategory.yellow]: Color.Yellow,
  [greenRedYellowResultTypeCategory.gray]: Color.Gray,
};

/**
 * Renders a pill for an outcome result.
 * Use this when you already have the result object directly.
 */
export const OutcomeResultPill: FC<{
  size: Size;
  result: OutcomeDefinition["results"][number];
  revenueAmount?: number | null;
}> = ({ size, result, revenueAmount }) => {
  const pillColor = categoryToPillColor[result.category] ?? Color.Gray;

  const label =
    revenueAmount !== undefined &&
    revenueAmount !== null &&
    revenueAmount > 0 &&
    result.category === greenRedYellowResultTypeCategory.green
      ? `${result.label} — $${Math.round(revenueAmount).toLocaleString()}`
      : result.label;

  return <Pill size={size} color={pillColor} label={label} />;
};

export const OutcomeValuePill: FC<OutcomeValuePillProps> = ({
  size,
  outcome,
  outcomeDefinition,
}) => {
  const valueId = getOutcomeValueFromDefinitionAndGrading(
    outcomeDefinition,
    outcome,
  );
  const result = getOutcomeResultForValueId(outcomeDefinition, valueId);

  if (!result) {
    return <span className="text-gray-400">--</span>;
  }

  const revenueAmount = isRevenueOutcomeDefinition(outcomeDefinition)
    ? getOutcomeAmountFromDefinitionAndGrading(outcomeDefinition, outcome)
    : undefined;

  return (
    <OutcomeResultPill
      size={size}
      result={result}
      revenueAmount={revenueAmount}
    />
  );
};
